import React, { useEffect, useRef, useState } from 'react';
import TacticEngine from './TacticEngine';
import StepPopover from './StepPopover';

export default function FieldCanvas({
    tactic,
    currentTime,
    editorMode,
    activeTool,
    selectedPlayerIndex,
    isFullscreen,
    readOnly,
    onPlayerDrag,
    onPlayerSelect,
    onPassDraw,
    onShotDraw,
    onKeyframeDrag,
    onDeleteKeyframe,
    onModeChange,
    onToolChange,
    onToggleFullscreen,
    stepView,
    currentStep,
    stepHint,
    onPuckDrop,
    onMoveEdit,
    onActionEdit,
    opponentCount,
    maxOpponents,
    onAddOpponent,
    onRemoveOpponent,
}) {
    const containerRef = useRef(null);
    const engineRef    = useRef(null);
    const [popover, setPopover] = useState(null);   // { target: {kind, playerIndex}, x, y }

    const handleArrowTap = (target, clientX, clientY) => {
        if (!target || !containerRef.current) { setPopover(null); return; }
        const rect = containerRef.current.getBoundingClientRect();
        setPopover({ target, x: clientX - rect.left, y: clientY - rect.top });
    };
    const arrowTapRef = useRef(handleArrowTap);
    arrowTapRef.current = handleArrowTap;

    // Close the popover whenever the step or mode changes
    useEffect(() => { setPopover(null); }, [currentStep, editorMode]);

    // Mount engine once
    useEffect(() => {
        if (!containerRef.current) return;
        const engine = new TacticEngine(containerRef.current, {
            onPlayerDrag:     (i, x, y)          => onPlayerDrag    && onPlayerDrag(i, x, y),
            onPlayerSelect:   (i)                 => onPlayerSelect  && onPlayerSelect(i),
            onPassDraw:       (from, to)           => onPassDraw      && onPassDraw(from, to),
            onShotDraw:       (from)               => onShotDraw      && onShotDraw(from),
            onKeyframeDrag:   (i, t, x, y)        => onKeyframeDrag  && onKeyframeDrag(i, t, x, y),
            onDeleteKeyframe: (i, t)              => onDeleteKeyframe && onDeleteKeyframe(i, t),
            onPuckDrop:       (target)            => onPuckDrop && onPuckDrop(target),
            onArrowTap:       (target, x, y)      => arrowTapRef.current(target, x, y),
        });
        engineRef.current = engine;
        return () => { engine.destroy(); engineRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep callbacks current without remounting
    useEffect(() => {
        if (!engineRef.current) return;
        engineRef.current.onPlayerDrag     = (i, x, y)   => onPlayerDrag    && onPlayerDrag(i, x, y);
        engineRef.current.onPlayerSelect   = (i)          => onPlayerSelect  && onPlayerSelect(i);
        engineRef.current.onPassDraw       = (from, to)   => onPassDraw      && onPassDraw(from, to);
        engineRef.current.onShotDraw       = (from)       => onShotDraw      && onShotDraw(from);
        engineRef.current.onKeyframeDrag   = (i, t, x, y) => onKeyframeDrag  && onKeyframeDrag(i, t, x, y);
        engineRef.current.onDeleteKeyframe = (i, t)       => onDeleteKeyframe && onDeleteKeyframe(i, t);
        engineRef.current.onPuckDrop       = (target)     => onPuckDrop && onPuckDrop(target);
    }, [onPlayerDrag, onPlayerSelect, onPassDraw, onShotDraw, onKeyframeDrag, onDeleteKeyframe, onPuckDrop]);

    // Drive engine on every render
    useEffect(() => {
        if (engineRef.current) {
            engineRef.current.update(tactic, currentTime, {
                editorMode,
                activeTool,
                selectedPlayerIndex: selectedPlayerIndex ?? -1,
                stepView: stepView || null,
            });
        }
    });

    const cursor = editorMode === 'advanced' && (activeTool === 'pase' || activeTool === 'tiro')
        ? 'crosshair'
        : 'grab';

    const MODES = [
        { key: 'setup',    label: 'Formación' },
        { key: 'steps',    label: 'Pasos' },
        { key: 'advanced', label: 'Avanzado' },
    ];

    const TOOLS = [
        { key: 'mover', label: '↕', title: 'Mover jugador' },
        { key: 'pase',  label: '↗', title: 'Dibujar pase' },
        { key: 'tiro',  label: '⚡', title: 'Dibujar tiro' },
    ];

    return (
        <div style={{ position: 'absolute', inset: 0 }}>
            {/* Three.js canvas */}
            <div ref={containerRef} style={{ width: '100%', height: '100%', cursor }} />

            {/* Fullscreen toggle — top-left */}
            {onToggleFullscreen && (
                <button
                    className="canvas-fullscreen-btn"
                    onClick={onToggleFullscreen}
                    title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                >
                    {isFullscreen ? '⊡' : '⛶'}
                </button>
            )}

            {/* Mode toggle — centered at top */}
            {!readOnly && (
                <div className="canvas-mode-bar">
                    {MODES.map(m => (
                        <button
                            key={m.key}
                            className={editorMode === m.key ? 'active' : ''}
                            onClick={() => onModeChange(m.key)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Tool toolbar — top-right, only in choreograph mode */}
            {!readOnly && editorMode === 'advanced' && (
                <div className="canvas-tool-bar">
                    {TOOLS.map(t => (
                        <button
                            key={t.key}
                            title={t.title}
                            className={activeTool === t.key ? 'active' : ''}
                            onClick={() => onToolChange(t.key)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Rival players for drills — Formación only */}
            {!readOnly && editorMode === 'setup' && onAddOpponent && (
                <div className="canvas-opponent-bar" title="Jugadores rivales para hacer ejercicios">
                    <span className="canvas-opponent-dot" />
                    <span>Rivales</span>
                    <button onClick={onRemoveOpponent} disabled={!opponentCount} title="Quitar rival">−</button>
                    <span className="canvas-opponent-count">{opponentCount}</span>
                    <button onClick={onAddOpponent} disabled={opponentCount >= maxOpponents} title="Añadir rival">+</button>
                </div>
            )}

            {/* Context hint — bottom center */}
            {!readOnly && (
                <div className="canvas-hint">
                    {editorMode === 'setup' && 'Arrastra los jugadores para ajustar la formación inicial · Arrastra el disco al jugador que empieza con él'}
                    {editorMode === 'steps' && stepHint}
                    {editorMode === 'advanced' && activeTool === 'mover' && (
                        selectedPlayerIndex != null
                            ? 'Arrastra para añadir movimiento · Haz clic en otro jugador para seleccionarlo'
                            : 'Haz clic en un jugador para seleccionarlo · Arrastra para moverlo'
                    )}
                    {editorMode === 'advanced' && activeTool === 'pase' && 'Arrastra de un jugador a otro para definir un pase'}
                    {editorMode === 'advanced' && activeTool === 'tiro' && 'Arrastra desde un jugador para definir un tiro a portería'}
                </div>
            )}

            {/* Arrow popover — speed / timing / delete (step mode) */}
            {popover && editorMode === 'steps' && (
                <StepPopover
                    target={popover.target}
                    x={popover.x}
                    y={popover.y}
                    step={currentStep}
                    tactic={tactic}
                    onMoveEdit={onMoveEdit}
                    onActionEdit={onActionEdit}
                    onClose={() => setPopover(null)}
                />
            )}
        </div>
    );
}
